import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useToast } from '../../components/ToastProvider';
import LoadingOverlay from '../../components/LoadingOverlay';
import GuestLayout from '../../components/GuestLayout';
import MenuSection from '../../components/customer/MenuSection';
import FilterControls from '../../components/customer/FilterControls';
import OrderSummary from '../../components/customer/OrderSummary';
import { useMenuItems } from '../../hooks/useMenuItems';
import { useOrder } from '../../hooks/useOrder';
import { useRestaurantDetails } from '../../hooks/useRestaurantDetails';
import { FILTER_OPTIONS } from '../../constants';
import { validateOrderForm } from '../../utils/validation';
import { API_ENDPOINTS } from '../../config/api';
import { parseMongoObjectId } from '../../utils/mongoId';
import { getDummyRestaurantById } from '../../data/dummyRestaurants';

const GuestMenu = () => {
    const { restaurantId, tableId } = useParams();
    const navigate = useNavigate();
    const addToast = useToast();
    
    // Custom hooks
    const { loading: menuLoading, error: menuError, refetch: refetchMenu, todaysSpecials, quicklyServed, getFilteredItems } = useMenuItems(restaurantId);
    const { order, addItemToOrder, removeItem, increaseQuantity, decreaseQuantity, clearOrder, totalAmount, serviceCharge } = useOrder();
    const { taxPercentage, calculateTaxAmount } = useRestaurantDetails(restaurantId);
    
    // Local state
    const [customerName, setCustomerName] = useState('');
    const [contactNumber, setContactNumber] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [filterCategory, setFilterCategory] = useState(FILTER_OPTIONS.CATEGORY.ALL);
    const [filterType, setFilterType] = useState(FILTER_OPTIONS.TYPE.ALL);
    const [searchQuery, setSearchQuery] = useState('');
    const [restaurantInfo, setRestaurantInfo] = useState(null);
    const [tableDetails, setTableDetails] = useState(null);
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    const [loginPromptFeature, setLoginPromptFeature] = useState('');
    /** Menu actions, search/refresh, and filters — hidden by default */
    const [showMenuTools, setShowMenuTools] = useState(false);

    const taxAmount = useMemo(() => 
        calculateTaxAmount(totalAmount), 
        [calculateTaxAmount, totalAmount]
    );

    // Memoized filtered items with search
    const filteredMenuItems = useMemo(() => {
        let items = getFilteredItems(filterCategory, filterType);
        
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            items = items.filter(item => 
                item.itemName.toLowerCase().includes(query) ||
                (item.itemDescription && item.itemDescription.toLowerCase().includes(query)) ||
                item.itemCategory.toLowerCase().includes(query)
            );
        }
        
        return items;
    }, [getFilteredItems, filterCategory, filterType, searchQuery]);

    // Search filtered special sections
    const searchFilteredTodaysSpecials = useMemo(() => {
        if (!searchQuery.trim()) return todaysSpecials;
        const query = searchQuery.toLowerCase();
        return todaysSpecials.filter(item => 
            item.itemName.toLowerCase().includes(query) ||
            (item.itemDescription && item.itemDescription.toLowerCase().includes(query)) ||
            item.itemCategory.toLowerCase().includes(query)
        );
    }, [todaysSpecials, searchQuery]);
    
    const searchFilteredQuicklyServed = useMemo(() => {
        if (!searchQuery.trim()) return quicklyServed;
        const query = searchQuery.toLowerCase();
        return quicklyServed.filter(item => 
            item.itemName.toLowerCase().includes(query) ||
            (item.itemDescription && item.itemDescription.toLowerCase().includes(query)) ||
            item.itemCategory.toLowerCase().includes(query)
        );
    }, [quicklyServed, searchQuery]);

    const isBrowseMode = !tableId || tableId === 'guest';
    const isDummyRestaurant = getDummyRestaurantById(restaurantId) != null;
    const menuToolsExpanded = isDummyRestaurant || showMenuTools;

    useEffect(() => {
        fetchRestaurantInfo();
        fetchTableDetails();
        if (!isBrowseMode) occupyTable();
        
        if (!isBrowseMode && tableId) {
            const handleBeforeUnload = () => {
                navigator.sendBeacon(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/api'}/tables/${tableId}/free`, '');
            };
            window.addEventListener('beforeunload', handleBeforeUnload);
            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
                freeTable();
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [restaurantId, tableId]);

    // Fetch restaurant info
    const fetchRestaurantInfo = async () => {
        try {
            const response = await axios.get(API_ENDPOINTS.RESTAURANT_BY_ID(restaurantId));
            setRestaurantInfo(response.data);
        } catch (error) {
            console.error('Failed to fetch restaurant info:', error);
            const fallback = getDummyRestaurantById(restaurantId);
            if (fallback) setRestaurantInfo(fallback);
        }
    };

    // Fetch table details
    const fetchTableDetails = async () => {
        try {
            if (tableId) {
                const response = await axios.get(API_ENDPOINTS.CUSTOMER_TABLE_DETAILS(tableId));
                setTableDetails(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch table details:', error);
        }
    };
    
    // Occupy table when customer accesses menu
    const occupyTable = async () => {
        try {
            if (tableId) {
                await axios.put(API_ENDPOINTS.TABLE_OCCUPY(tableId));
            }
        } catch (error) {
            console.error('Failed to occupy table:', error);
        }
    };
    
    // Free table when customer leaves
    const freeTable = async () => {
        try {
            if (tableId) {
                await axios.put(API_ENDPOINTS.TABLE_FREE(tableId));
            }
        } catch (error) {
            console.error('Failed to free table:', error);
        }
    };

    // Handle login prompt for restricted features
    const handleRestrictedFeature = (featureName) => {
        setLoginPromptFeature(featureName);
        setShowLoginPrompt(true);
    };

    // Navigate to customer login
    const handleLogin = () => {
        localStorage.setItem('guestOrder', JSON.stringify(order));
        localStorage.setItem('guestCustomerInfo', JSON.stringify({ customerName, contactNumber, message }));
        localStorage.setItem('restaurantId', restaurantId);
        if (tableId) localStorage.setItem('tableId', tableId);
        navigate('/customer-login');
    };

    // Place Order as guest
    const placeOrder = async () => {
        const validation = validateOrderForm(customerName, contactNumber, order);
        if (!validation.isValid) {
            addToast(validation.errors.join(', '), "warning");
            return;
        }
        
        try {
            setLoading(true);
            const finalAmount = Number(totalAmount) + Number(serviceCharge) + Number(taxAmount);
            const resolvedTableId = parseMongoObjectId(tableId);
            const isTableOrder = !isBrowseMode && resolvedTableId;
            const payload = {
                restaurantId: restaurantId,
                customerName,
                mobileNumber: contactNumber ? contactNumber : "Guest",
                message,
                items: order,
                serviceAmount: serviceCharge,
                taxPercentage: taxAmount,
                finalAmount,
                orderDate: new Date(),
                isGuestOrder: true,
                orderType: isTableOrder ? 'table' : 'counter',
            };
            if (isTableOrder) payload.tableId = resolvedTableId;
            await axios.post(API_ENDPOINTS.ORDERS_ADD, payload);
            
            addToast("Order placed successfully!", "success");
            
            // Clear form and order
            clearOrder();
            setCustomerName('');
            setContactNumber('');
            setMessage('');
        } catch (error) {
            addToast("Failed to place order", "error");
            console.error('Order placement error:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <GuestLayout>
            {(loading || menuLoading) && <LoadingOverlay />}
            
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-xl shadow-sm border-b border-stone-200 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2.5 sm:py-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                            {restaurantInfo?.logo && (
                                <motion.img
                                    src={restaurantInfo.logo}
                                    alt={restaurantInfo.name}
                                    className="h-9 w-9 sm:h-11 sm:w-11 rounded-xl shadow-md object-cover flex-shrink-0 border border-stone-100"
                                    whileHover={{ scale: 1.05 }}
                                    transition={{ duration: 0.2 }}
                                />
                            )}
                            <div className="min-w-0 flex-1">
                                <h1 className="text-base sm:text-xl font-bold text-stone-800 truncate">
                                    {restaurantInfo?.name || 'Restaurant'}
                                </h1>
                                <p className="text-xs sm:text-sm text-stone-600 flex items-center gap-1 truncate">
                                    <span>🪑</span>
                                    <span className="truncate">
                                        {isBrowseMode ? 'Browse menu' : `Table: ${tableDetails?.displayName || tableDetails?.tableName || tableDetails?.tableNumber || 'Loading...'}`}
                                    </span>
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <Link
                                to="/"
                                className="inline-flex items-center justify-center px-3 py-2 sm:px-4 rounded-xl border border-stone-200 text-stone-700 hover:border-stone-300 hover:bg-stone-50 transition-all duration-300 text-xs sm:text-sm font-medium whitespace-nowrap min-h-[2.25rem] sm:min-h-[2.5rem]"
                            >
                                Home
                            </Link>
                            {!isDummyRestaurant && (
                                <motion.button
                                    type="button"
                                    onClick={handleLogin}
                                    className="inline-flex items-center justify-center px-3 py-2 sm:px-4 rounded-xl bg-amber-500 hover:bg-amber-600 text-white transition-all duration-300 shadow-md shadow-amber-500/20 text-xs sm:text-sm font-medium whitespace-nowrap min-h-[2.25rem] sm:min-h-[2.5rem]"
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    🔐 Login
                                </motion.button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-4 lg:py-6 w-full min-w-0 overflow-x-hidden relative z-10">
                {/* Menu Section */}
                <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-stone-200/80 mb-4 sm:mb-6 lg:mb-8 overflow-hidden">
                    <div className="border-b border-stone-200">
                        <div className="p-3 sm:p-4">
                            <div className="flex items-center justify-between gap-3 min-h-[2.25rem]">
                                <h2 className="text-base sm:text-lg font-bold text-stone-800 flex items-center gap-2 min-w-0">
                                    <span className="flex-shrink-0">🍽️</span>
                                    <span>Menu</span>
                                </h2>
                                {!isDummyRestaurant && (
                                    <button
                                        type="button"
                                        aria-expanded={showMenuTools}
                                        aria-controls="guest-menu-tools"
                                        onClick={() => setShowMenuTools((v) => !v)}
                                        className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold border border-stone-200 bg-white text-stone-700 hover:bg-stone-50 transition-colors"
                                    >
                                        {showMenuTools ? 'Hide' : 'Show'}
                                    </button>
                                )}
                            </div>
                            {menuToolsExpanded && (
                                <div id="guest-menu-tools" className="mt-3 space-y-3">
                                {!isDummyRestaurant && (
                                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                        <button
                                            type="button"
                                            onClick={() => handleRestrictedFeature('My Orders')}
                                            className="px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm bg-stone-100 text-stone-700 rounded-lg hover:bg-stone-200 border border-stone-200 transition-colors whitespace-nowrap"
                                        >
                                            📋 My Orders
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleRestrictedFeature('Profile')}
                                            className="px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm bg-stone-100 text-stone-700 rounded-lg hover:bg-stone-200 border border-stone-200 transition-colors whitespace-nowrap"
                                        >
                                            👤 Profile
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleRestrictedFeature('Offers')}
                                            className="px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm bg-stone-100 text-stone-700 rounded-lg hover:bg-stone-200 border border-stone-200 transition-colors whitespace-nowrap"
                                        >
                                            🎁 Offers
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleRestrictedFeature('Call Waiter')}
                                            className="px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm bg-stone-100 text-stone-700 rounded-lg hover:bg-stone-200 border border-stone-200 transition-colors whitespace-nowrap"
                                        >
                                            🔔 Call Waiter
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => navigate(`/reserve/${restaurantId}`)}
                                            className="px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm bg-amber-50 text-amber-800 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors whitespace-nowrap font-medium"
                                        >
                                            📅 Reserve Table
                                        </button>
                                    </div>
                                )}

                            <div
                                className={
                                    isDummyRestaurant
                                        ? 'flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3'
                                        : 'flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 pt-3 border-t border-stone-100'
                                }
                            >
                                <div className="relative w-full max-w-md flex-1 min-w-0">
                                    <input
                                        type="text"
                                        placeholder="Search menu items..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-9 pr-8 py-2 sm:py-2.5 border border-stone-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-300 bg-stone-50 text-sm text-stone-900 placeholder-stone-400"
                                    />
                                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">
                                        🔍
                                    </div>
                                    {searchQuery && (
                                        <button
                                            type="button"
                                            onClick={() => setSearchQuery('')}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 text-sm"
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => refetchMenu()}
                                    disabled={menuLoading}
                                    aria-label="Refresh menu"
                                    title="Refresh menu"
                                    className="inline-flex items-center justify-center gap-1.5 shrink-0 rounded-lg sm:rounded-xl border border-stone-200 bg-white px-3 py-2 sm:py-2.5 text-sm font-semibold text-stone-700 hover:bg-stone-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed w-full sm:w-auto"
                                >
                                    <span className={menuLoading ? 'inline-block animate-spin' : ''} aria-hidden>🔄</span>
                                    Refresh menu
                                </button>
                            </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="p-3 sm:p-4">
                        {menuError && (
                            <motion.div
                                className="bg-red-50 border border-red-200 text-red-700 px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg sm:rounded-xl mb-4 sm:mb-6 text-sm"
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                {menuError}
                            </motion.div>
                        )}

                        <div className="space-y-6 sm:space-y-8 min-w-0">
                            <MenuSection
                                title="Quickly Served"
                                items={searchFilteredQuicklyServed}
                                onAddToOrder={addItemToOrder}
                                isGuest={true}
                            />

                            <MenuSection
                                title="Today's Specials"
                                items={searchFilteredTodaysSpecials}
                                onAddToOrder={addItemToOrder}
                                isGuest={true}
                            />

                            {searchQuery.trim() && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <div className="flex items-center gap-2">
                                            <span className="text-amber-600 text-base">🔍</span>
                                            <span className="text-stone-800 font-medium text-sm sm:text-base">
                                                Search results for &quot;{searchQuery}&quot;
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setSearchQuery('')}
                                            className="text-amber-600 hover:text-amber-700 font-medium text-xs sm:text-sm"
                                        >
                                            Clear search
                                        </button>
                                    </div>
                                </div>
                            )}
                            
                            {menuToolsExpanded && (
                                <FilterControls 
                                    filterCategory={filterCategory}
                                    filterType={filterType}
                                    onCategoryChange={setFilterCategory}
                                    onTypeChange={setFilterType}
                                />
                            )}

                            <MenuSection 
                                title="Menu" 
                                items={filteredMenuItems} 
                                onAddToOrder={addItemToOrder}
                                isGuest={true}
                            />
                        </div>
                    </div>
                </div>

                {/* Order Summary */}
                {order.length > 0 && (
                    <motion.div
                        className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-stone-200/80 p-3 sm:p-4 lg:p-6 overflow-hidden min-w-0"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <h2 className="text-sm sm:text-base font-bold mb-3 sm:mb-4 text-stone-800 flex items-center gap-2">
                            <span className="text-amber-600">🛒</span>
                            <span>Your Order</span>
                        </h2>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-6">
                            <div>
                                <label className="block text-xs sm:text-sm font-semibold mb-1.5 text-stone-700">Name *</label>
                                <input
                                    type="text"
                                    value={customerName}
                                    onChange={(e) => setCustomerName(e.target.value)}
                                    className="w-full border border-stone-200 bg-stone-50 px-3 py-2.5 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all text-sm"
                                    placeholder="Enter your name"
                                />
                            </div>
                            <div>
                                <label className="block text-xs sm:text-sm font-semibold mb-1.5 text-stone-700">Contact (optional)</label>
                                <input
                                    type="tel"
                                    value={contactNumber}
                                    onChange={(e) => setContactNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                    className="w-full border border-stone-200 bg-stone-50 px-3 py-2.5 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all text-sm"
                                    placeholder="10-digit mobile"
                                    maxLength="10"
                                />
                            </div>
                        </div>

                        <div className="mb-4 sm:mb-6">
                            <label className="block text-xs sm:text-sm font-semibold mb-1.5 text-stone-700">Special Instructions</label>
                            <textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                className="w-full border border-stone-200 bg-stone-50 px-3 py-2.5 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all text-sm"
                                placeholder="Any special requests or dietary requirements?"
                                rows="2"
                            />
                        </div>

                        <OrderSummary
                            order={order}
                            totalAmount={totalAmount}
                            serviceCharge={serviceCharge}
                            taxPercentage={taxPercentage}
                            taxAmount={taxAmount}
                            onIncreaseQuantity={increaseQuantity}
                            onDecreaseQuantity={decreaseQuantity}
                            onRemoveItem={removeItem}
                        />

                        <div className="flex flex-col sm:flex-row gap-2 mt-4 sm:mt-5">
                            <motion.button
                                onClick={placeOrder}
                                disabled={loading || order.length === 0}
                                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 sm:py-2.5 sm:px-4 rounded-lg font-semibold text-xs sm:text-sm transition-all duration-300 disabled:bg-stone-400 disabled:cursor-not-allowed shadow shadow-amber-500/20 inline-flex items-center justify-center gap-1.5"
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.99 }}
                            >
                                {loading ? (
                                    <>
                                        <div className="animate-spin rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 border-2 border-white border-t-transparent" />
                                        <span>Placing Order...</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="text-sm" aria-hidden="true">🚀</span>
                                        <span>Place Order</span>
                                    </>
                                )}
                            </motion.button>
                            <motion.button
                                onClick={handleLogin}
                                className="w-full sm:w-auto px-3 py-2 sm:py-2.5 sm:px-4 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg font-medium text-xs sm:text-sm transition-all duration-300 border border-stone-200 inline-flex items-center justify-center gap-1.5"
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.99 }}
                            >
                                <span className="text-sm" aria-hidden="true">🔐</span>
                                <span>Login for Offers & More</span>
                            </motion.button>
                        </div>
                    </motion.div>
                )}
            </div>

            {/* Login Prompt Modal */}
            {showLoginPrompt && (
                <div className="fixed inset-0 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4 overflow-y-auto">
                    <motion.div
                        className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-md w-full p-4 sm:p-6 my-auto overflow-x-hidden min-w-0 border border-stone-200"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                    >
                        <div className="text-center">
                            <div className="text-4xl sm:text-5xl mb-3 sm:mb-4">🔐</div>
                            <h3 className="text-xl sm:text-2xl font-bold text-stone-800 mb-2">Login Required</h3>
                            <p className="text-stone-600 text-sm sm:text-base mb-4 sm:mb-6">
                                To access <strong>{loginPromptFeature}</strong> and enjoy exclusive offers, discounts, and a personalized experience, please log in.
                            </p>

                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 text-left">
                                <h4 className="font-semibold text-stone-800 mb-2 text-sm sm:text-base">🎁 Login benefits</h4>
                                <ul className="text-xs sm:text-sm text-stone-600 space-y-1">
                                    <li>• Exclusive discounts & offers</li>
                                    <li>• Order history & favorites</li>
                                    <li>• Loyalty rewards</li>
                                    <li>• Call waiter service</li>
                                    <li>• Personalized recommendations</li>
                                </ul>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                                <button
                                    type="button"
                                    onClick={handleLogin}
                                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-white px-5 py-2.5 sm:py-3 rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 shadow-md shadow-amber-500/20"
                                >
                                    Login / Sign Up
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowLoginPrompt(false)}
                                    className="px-5 py-2.5 sm:py-3 bg-stone-100 text-stone-700 rounded-xl font-medium text-sm hover:bg-stone-200 transition-colors"
                                >
                                    Continue as Guest
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </GuestLayout>
    );
};

export default GuestMenu;